import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Img, PageLoader, RedactContext, RedactOverlay, RedactedText, Reveal, SectionLabel, StatusBadge } from "@/components/common";
import { MediaBlock, SocialGroup } from "@/components/media/MediaBlock";
import { BACKEND_URL, imgUrl, useFetch } from "@/lib/api";
import { useSite } from "@/lib/site";
import { useSeo } from "@/lib/seo";
import { ProjectPullQuote } from "@/components/testimonials/ProjectPullQuote";
import NotFound from "./NotFound";

const SECTIONS = [
  ["overview", "The Idea"], ["challenge", "The Problem"], ["solution", "The Solution"], ["what_i_created", "What I Created"],
  ["design_notes", "Design"], ["technology", "Technology"], ["key_features", "Key Features"], ["results", "Final Result"],
];

const hasContent = (v) => (Array.isArray(v) ? v.length > 0 : Boolean(v && String(v).trim()));

const groupBlocks = (blocks) =>
  blocks.reduce((acc, b) => {
    const last = acc[acc.length - 1];
    if (b.type.startsWith("social_") && last?.social) last.blocks.push(b);
    else acc.push(b.type.startsWith("social_") ? { social: true, blocks: [b] } : { block: b });
    return acc;
  }, []);

const SectionBody = ({ field, value }) => {
  if (field === "technology")
    return (
      <ul className="flex flex-wrap gap-2">
        {value.map((t) => <li key={t} className="rounded-full border border-line/20 px-4 py-2 font-mono text-xs text-fg">{t}</li>)}
      </ul>
    );
  if (field === "what_i_created")
    return (
      <ol className="divide-y divide-line/10 border-y border-line/10">
        {value.map((t, i) => <li key={t} className="flex gap-6 py-4 text-base md:text-lg"><span className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</span>{t}</li>)}
      </ol>
    );
  if (field === "key_features")
    return (
      <ul className="grid gap-px overflow-hidden rounded-md border border-line/10 bg-line/10 sm:grid-cols-2">
        {value.map((t, i) => <li key={t} className="bg-canvas p-6"><span className="font-mono text-xs text-turq">F.{String(i + 1).padStart(2, "0")}</span><p className="mt-3 text-base">{t}</p></li>)}
      </ul>
    );
  const big = field === "overview" || field === "results";
  return <p className={big ? "font-display text-2xl leading-snug md:text-4xl" : "text-base leading-relaxed text-fg/85 md:text-lg"}><RedactedText text={value} /></p>;
};

const NavCard = ({ item, dir }) => (
  <Link to={`/work/${item.slug}`} data-testid={`case-${dir}-link`} className="group relative block overflow-hidden bg-surface p-8 md:p-12">
    <RedactContext.Provider value={item.status === "CONFIDENTIAL"}>
      <Img src={item.cover_image} alt="" sizes="50vw" className="absolute inset-0 h-full w-full object-cover opacity-25 transition-[opacity,transform] duration-700 group-hover:scale-105 group-hover:opacity-45" />
    </RedactContext.Provider>
    <div className={`relative flex flex-col gap-4 ${dir === "next" ? "items-end text-right" : ""}`}>
      <span className="label-mono flex items-center gap-2">{dir === "prev" ? <><ArrowLeft className="h-3.5 w-3.5" /> Previous</> : <>Next <ArrowRight className="h-3.5 w-3.5" /></>}</span>
      <span className="font-display text-3xl transition-colors group-hover:text-turq md:text-5xl">{item.name}</span>
    </div>
  </Link>
);

export default function CaseStudy() {
  const { slug } = useParams();
  const { catById, settings } = useSite();
  const { data, loading, error } = useFetch(`/projects/${slug}`);
  const p0 = data?.project;
  const visible = p0 && p0.status !== "CONFIDENTIAL";
  useSeo({
    title: p0 && `${p0.name} — ${settings?.name}`,
    description: p0?.tagline || p0?.overview,
    image: visible ? p0.cover_image : undefined,
    jsonLd: p0 && { "@context": "https://schema.org", "@type": "CreativeWork", name: p0.name, description: p0.tagline, url: window.location.href, image: visible ? imgUrl(p0.cover_image) : undefined, creator: { "@type": "Person", name: settings?.name, jobTitle: settings?.title }, keywords: p0.technology.join(", ") },
  });
  const share = async () => {
    const url = `${BACKEND_URL}/api/og/work/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied", { description: "Includes a rich preview for social platforms." });
    } catch {
      toast(url);
    }
  };
  if (loading && !data) return <PageLoader />;
  if (error || !data) return <NotFound message="That case study couldn't be found." />;
  const { project: p, prev, next } = data;
  const confidential = p.status === "CONFIDENTIAL";
  const sections = SECTIONS.filter(([f]) => hasContent(p[f]));
  const blocks = groupBlocks(p.media_blocks);
  const quoteAfter = sections.some(([f]) => f === "results") ? "results" : sections[sections.length - 1]?.[0];
  const slots = blocks.map((_, j) => Math.max(1, Math.round(((j + 1) * sections.length) / (blocks.length + 1))));
  const renderBlock = (b, j) => (b.social ? <SocialGroup key={`b${j}`} blocks={b.blocks} projectName={p.name} index={j} /> : <MediaBlock key={`b${j}`} block={b.block} projectName={p.name} index={j} />);
  const meta = [
    ["Client / Concept", confidential ? <span className="inline-flex items-center gap-2"><Lock className="h-3.5 w-3.5 text-gold" aria-hidden />Client withheld</span> : p.client_or_concept],
    ["Category", p.category_ids.map((id) => catById[id]?.name).filter(Boolean).join(", ")],
    ["My Role", p.my_role],
    ["Year", p.year],
  ].filter(([, v]) => v);

  return (
    <RedactContext.Provider value={confidential}>
      <article data-testid="case-study-page" data-slug={p.slug}>
        <header className="relative flex min-h-[88svh] items-end overflow-hidden pt-[72px]">
          <Img src={p.hero_image || p.cover_image} alt={confidential ? "Confidential project hero (obscured)" : p.image_alt || `${p.name} hero`} eager sizes="100vw" className="absolute inset-0 h-full w-full object-cover object-top" />
          {confidential && <RedactOverlay />}
          <div className="absolute inset-0 bg-gradient-to-t from-canvas via-canvas/50 to-black/20" />
          <div className="container-x relative z-20 pb-14 md:pb-20">
            <Link to="/work" data-testid="case-back-link" className="label-mono inline-flex items-center gap-2 hover:text-turq"><ArrowLeft className="h-3.5 w-3.5" aria-hidden />All work</Link>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <StatusBadge status={p.status} />
              {p.category_ids.map((id) => catById[id] && <span key={id} className="label-mono text-fg/80">{catById[id].number} {catById[id].name}</span>)}
            </div>
            <h1 className="mt-6 max-w-5xl font-display text-5xl leading-[0.92] sm:text-6xl lg:text-8xl" data-testid="case-title">{p.name}</h1>
            {p.tagline && <p className="mt-6 max-w-2xl text-lg text-fg/85 md:text-xl">{p.tagline}</p>}
            <button type="button" onClick={share} data-testid="case-share-button" className="label-mono mt-8 inline-flex items-center gap-2 rounded-full border border-line/25 px-4 py-2 text-fg hover:border-turq hover:text-turq"><Share2 className="h-3.5 w-3.5" aria-hidden />Share</button>
          </div>
        </header>

        {confidential && (
          <div className="container-x" data-testid="confidential-notice">
            <div className="flex items-start gap-4 rounded-md border border-gold/40 bg-gold/5 p-5 text-sm">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden />
              <p><span className="font-medium text-fg">Confidential engagement.</span> <span className="text-silver">Client name, identifying details and imagery are obscured under NDA. Full walkthrough available on request.</span></p>
            </div>
          </div>
        )}

        <section className="container-x py-12" aria-label="Project details">
          <dl className="grid gap-8 border-y border-line/10 py-10 sm:grid-cols-2 lg:grid-cols-4" data-testid="case-meta">
            {meta.map(([k, v]) => (
              <div key={k}><dt className="label-mono">{k}</dt><dd className="mt-3 text-sm leading-relaxed">{v}</dd></div>
            ))}
          </dl>
          {p.technology.length > 0 && (
            <p className="mt-6 text-sm text-silver" data-testid="case-tools"><span className="label-mono mr-3">Tools</span>{p.technology.join(" · ")}</p>
          )}
        </section>

        {sections.map(([field, label], i) => (
          <div key={field}>
            <section className="container-x grid gap-6 py-14 md:grid-cols-12 md:py-20" data-testid={`case-section-${field}`}>
              <div className="md:col-span-4"><SectionLabel number={String(i + 1).padStart(2, "0")}>{label}</SectionLabel></div>
              <Reveal className="md:col-span-8"><SectionBody field={field} value={p[field]} /></Reveal>
            </section>
            {field === quoteAfter && <ProjectPullQuote projectId={p.id} />}
            {blocks.map((b, j) => (slots[j] === i + 1 ? renderBlock(b, j) : null))}
          </div>
        ))}
        {sections.length === 0 && blocks.map(renderBlock)}
        {sections.length === 0 && <ProjectPullQuote projectId={p.id} />}

        {(prev || next) && (
          <nav className="mt-16 grid gap-px border-t border-line/10 bg-line/10 md:grid-cols-2" aria-label="More projects" data-testid="case-pagination">
            {prev && <NavCard item={prev} dir="prev" />}
            {next && <NavCard item={next} dir="next" />}
          </nav>
        )}
      </article>
    </RedactContext.Provider>
  );
}
